/**
 * QUIPWA — StateReplicator
 *
 * Keeps the local `_state.db` and its copies in Drive converged:
 *
 *   1. pull   — for each remote copy whose Drive `version` changed since we
 *               last saw it: download, open in memory, export rows
 *   2. merge  — mergeReplicatedStates(local, …remotes): conflict-free
 *   3. apply  — write only the rows that changed, in one local transaction
 *   4. push   — upload where the remote copy lacks something; re-check the
 *               remote `version` right before, and start over if it moved
 *
 * Remote copies ("locations"):
 *
 *   appdata — `appDataFolder/_state.db`, hidden from the user, the primary.
 *             Needs the drive.appdata scope; without it we fall back to legacy.
 *   legacy  — `QUIPWA/_state.db`, where every earlier version kept it. It is
 *             merged as one more replica and kept updated while it exists, so
 *             a device still on an older app version keeps syncing. It is
 *             never created by this version.
 *
 * Comparing Drive `version`s (a server-side counter) replaced comparing a
 * file's modifiedTime with a timestamp from this device's clock, which missed
 * or invented changes whenever device clocks disagreed.
 *
 * See docs/modulos/sincronizacion.md.
 */

import type { DriveService, DriveFile } from '../drive/drive-service.ts'
import { APP_DATA_FOLDER } from '../drive/drive-service.ts'
import { isDriveError } from '../drive/drive-errors.ts'
import type { SqlJsAdapter } from '../db/sqljs-adapter.ts'
import { openStateSnapshot } from '../db/db-factory.ts'
import type { KvStore } from '../local/kv-store.ts'
import { KV } from '../local/kv-store.ts'
import type { HybridClock } from '../replication/hlc.ts'
import { applyReplicationChanges, exportReplicatedState } from '../replication/state-io.ts'
import { countChanges, diffReplicatedStates, mergeReplicatedStates } from '../replication/state-merge.ts'
import type { ReplicatedState } from '../replication/state-tables.ts'

export const STATE_FILE_NAME = '_state.db'
export const STATE_MIME = 'application/x-sqlite3'
const MAX_ROUNDS = 3

export type StateLocationKey = 'appdata' | 'legacy'

interface Bookmark {
  fileId: string
  version: string
}

interface Location {
  key: StateLocationKey
  parentId: string
  /** May this location's file be created when missing? */
  creatable: boolean
}

export interface StateSyncReport {
  /** Rows changed locally by the merge (0 = nothing new from Drive). */
  localChanges: number
  /** Locations uploaded to. */
  pushed: StateLocationKey[]
  /** True when appDataFolder was not usable because the token lacks the scope. */
  appDataUnavailable: boolean
}

export interface StateReplicatorDeps {
  drive: DriveService
  state: SqlJsAdapter
  kv: KvStore
  clock: HybridClock
  /** Resolves the visible `QUIPWA` folder (only needed for the legacy copy). */
  rootFolderId: () => Promise<string>
}

/** Raised when a remote copy changed between our download and our upload. */
class RemoteMovedError extends Error {}

export class StateReplicator {
  private readonly deps: StateReplicatorDeps
  /** Bumped by every local write; compared before/after a push. */
  private generation = 0
  private persistedDirty: boolean | null = null

  constructor(deps: StateReplicatorDeps) {
    this.deps = deps
    deps.state.onChange(() => this.noteLocalChange())
  }

  /** True when local writes have not reached Drive yet (survives reloads). */
  async hasUnpushedChanges(): Promise<boolean> {
    if (this.persistedDirty === null) {
      this.persistedDirty = (await this.deps.kv.get<boolean>(KV.stateDirty)) ?? false
    }
    return this.persistedDirty
  }

  async sync(): Promise<StateSyncReport> {
    for (let round = 1; ; round++) {
      try {
        return await this.syncOnce()
      } catch (err) {
        if (err instanceof RemoteMovedError && round < MAX_ROUNDS) continue
        throw err
      }
    }
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private noteLocalChange(): void {
    this.generation += 1
    if (this.persistedDirty !== true) {
      this.persistedDirty = true
      void this.deps.kv.set(KV.stateDirty, true).catch(() => undefined)
    }
  }

  private async syncOnce(): Promise<StateSyncReport> {
    const { locations, appDataUnavailable } = await this.resolveLocations()

    // 1. Pull every remote copy that changed since we last saw it.
    const local = await exportReplicatedState(this.deps.state)
    const remote = new Map<StateLocationKey, { file: DriveFile | null; state: ReplicatedState | 'unchanged' | null }>()
    let merged = local
    for (const loc of locations) {
      const file = await this.findRemote(loc)
      if (!file) {
        remote.set(loc.key, { file: null, state: null })
        continue
      }
      const bookmark = await this.deps.kv.get<Bookmark>(KV.stateBookmark(loc.key))
      if (bookmark?.fileId === file.id && bookmark.version === file.version) {
        remote.set(loc.key, { file, state: 'unchanged' })
        continue
      }
      const state = await this.download(file.id)
      remote.set(loc.key, { file, state })
      merged = mergeReplicatedStates(merged, state)
    }

    // 2–3. Apply locally what the merge changed.
    const localDiff = diffReplicatedStates(local, merged)
    const localChanges = countChanges(localDiff)
    if (localChanges > 0) {
      await applyReplicationChanges(this.deps.state, localDiff)
      for (const row of merged.deckInstances) this.deps.clock.observe(row.updated_at)
      for (const row of merged.reviewStates) this.deps.clock.observe(row.updated_at)
    }

    // 4. Push where the remote copy is behind.
    const captured = this.generation
    const dirty = await this.hasUnpushedChanges()
    const pushed: StateLocationKey[] = []
    for (const loc of locations) {
      const { file, state } = remote.get(loc.key)!
      const behind =
        file === null ? loc.creatable
        : state === 'unchanged' ? dirty || localChanges > 0
        : countChanges(diffReplicatedStates(state!, merged)) > 0

      if (behind) {
        await this.upload(loc, file)
        pushed.push(loc.key)
      } else if (file && state !== 'unchanged') {
        // Merged in and nothing to add: remember this version as seen.
        await this.deps.kv.set(KV.stateBookmark(loc.key), { fileId: file.id, version: file.version ?? '' })
      }
    }

    if (this.generation === captured) await this.markClean()
    await this.deps.state.flushNow()
    return { localChanges, pushed, appDataUnavailable }
  }

  private async resolveLocations(): Promise<{ locations: Location[]; appDataUnavailable: boolean }> {
    const legacyParent = await this.deps.rootFolderId()
    let appDataUnavailable = false
    try {
      // Probe the scope with a cheap listing; the answer is reused by findRemote.
      await this.deps.drive.findFile(STATE_FILE_NAME, APP_DATA_FOLDER)
    } catch (err) {
      if (!isDriveError(err, 'insufficient_scope')) throw err
      appDataUnavailable = true
    }
    const locations: Location[] = appDataUnavailable
      ? [{ key: 'legacy', parentId: legacyParent, creatable: true }]
      : [
          { key: 'appdata', parentId: APP_DATA_FOLDER, creatable: true },
          { key: 'legacy', parentId: legacyParent, creatable: false },
        ]
    return { locations, appDataUnavailable }
  }

  /** The remote file, preferring the bookmarked id (cheap, exact) over a search by name. */
  private async findRemote(loc: Location): Promise<DriveFile | null> {
    const bookmark = await this.deps.kv.get<Bookmark>(KV.stateBookmark(loc.key))
    if (bookmark) {
      const byId = await this.deps.drive.findById(bookmark.fileId)
      if (byId && !byId.trashed) return byId
    }
    return this.deps.drive.findFile(STATE_FILE_NAME, loc.parentId)
  }

  private async download(fileId: string): Promise<ReplicatedState> {
    const snapshot = await openStateSnapshot(await this.deps.drive.downloadFile(fileId))
    try {
      return await exportReplicatedState(snapshot)
    } finally {
      await snapshot.close()
    }
  }

  private async upload(loc: Location, file: DriveFile | null): Promise<void> {
    // Narrow the race window: if another device uploaded since we looked,
    // merge that first instead of overwriting it.
    if (file) {
      const current = await this.deps.drive.getFileMeta(file.id)
      if (current.version !== file.version) throw new RemoteMovedError()
    }
    const bytes = await this.deps.state.exportToArrayBuffer()
    const result = file
      ? await this.deps.drive.updateFileContent(file.id, bytes, STATE_MIME)
      : await this.deps.drive.createFile({ name: STATE_FILE_NAME, parentId: loc.parentId, mimeType: STATE_MIME, content: bytes })
    await this.deps.kv.set(KV.stateBookmark(loc.key), { fileId: result.id, version: result.version ?? '' })
  }

  private async markClean(): Promise<void> {
    this.persistedDirty = false
    await this.deps.kv.set(KV.stateDirty, false)
  }
}
